<script>
import Layout from '@layouts/main.vue'
import {
  authComputed,
  cityMethods,
  cityComputed,
  documentMethods,
  volunteerMethods,
  volunteerComputed,
} from '@state/helpers'

export default {
  page() {
    return {
      title: this.isEdit ? this.form.title : 'Sukurti dokumentą',
    }
  },

  components: { Layout },

  data() {
    return {
      form: {
        title: '',
        description: '',
        city: '',
        role: '',
        editors: [],
      },
      editors: [],
      dialogs: {
        volunteer: {
          visible: false,
          volunteerId: null,
        },
      },
      errorMessage: null,
      isLoading: false,
      failedField: null,
    }
  },

  computed: {
    ...authComputed,

    ...cityComputed,

    ...volunteerComputed,

    documentId() {
      return this.$route.params.id
    },

    isEdit() {
      return !!this.documentId
    },

    roles() {
      const options = [
        {
          id: 'volunteer',
          label: 'Savanoris',
        },
        {
          id: 'coordinator',
          label: 'Koordinatorius',
        },
      ]
      if (this.currentUser.role === 'admin') {
        options.push({
          id: 'admin',
          label: 'Administratorius',
        })
      }
      return options
    },

    filteredEditors() {
      if (!this.volunteers) return []

      return this.volunteerOptions.filter(
        (v) => !this.form.editors.includes(v.id),
      )
    },
  },

  async created() {
    await this.fetchAllVolunteers({ sort: 'first_name', per_page: 10000 })
    this.loadDocumentIfEdit()
    this.fetchAllCities()
  },

  methods: {
    ...documentMethods,

    ...cityMethods,

    ...volunteerMethods,

    loadDocumentIfEdit() {
      if (this.isEdit) {
        this.viewDocument(this.documentId).then((doc) => {
          Object.keys(doc).forEach((field) => {
            this.form[field] = doc[field]
          })

          if (this.form.editors) {
            this.form.editors.forEach((id) => {
              const editor = this.volunteerOptions.find((o) => o.id === id)
              this.editors.push(editor)
            })
          }
        })
      }
    },

    onSubmitForm() {
      const action = this.isEdit ? this.updateDocument : this.createDocument
      return action({
        id: this.documentId,
        ...this.form,
      })
        .then((token) => {
          this.resetFormState()
          this.$router.push({ name: 'documents' })
        })
        .catch((error) => {
          this.isLoading = false
          this.errorMessage = 'Formoje yra klaidų.'
          this.failedField = error.response.data.error.replace(/.*: /, '')
        })
    },

    onClickDelete() {
      if (confirm('Ar tikrai norite ištrinti įrašą?')) {
        this.deleteDocument(this.documentId).then(() => {
          this.$router.push({ name: 'documents' })
        })
      }
    },

    resetFormState() {
      this.isLoading = true
      this.errorMessage = null
      this.failedField = null
    },

    onClickAddEditor() {
      this.dialogs.volunteer.visible = true
    },

    onSubmitEditor() {
      this.form.editors.push(this.dialogs.volunteer.volunteerId)

      const editor = this.volunteerOptions.find(
        (o) => o.id === this.dialogs.volunteer.volunteerId,
      )
      this.editors.push(editor)

      this.dialogs.volunteer.visible = false
    },

    onClickRemoveEditor(editorId) {
      const editor = this.volunteerOptions.find((o) => o.id === editorId)
      if (
        confirm(
          `Ar tikrai norite pašalinti redagavimo teises - ${editor.label}?`,
        )
      ) {
        this.form.editors = this.form.editors.filter((x) => x !== editorId)
        this.editors = this.editors.filter((x) => x.id !== editorId)
      }
    },
  },
}
</script>

<template>
  <Layout>
    <base-page-title>
      <template v-slot:title>
        <h1 class="text-2xl font-bold">
          <template v-if="isEdit">{{ form.title }}</template>
          <template v-else> Sukurti dokumentą </template>
        </h1>
      </template>
      <template v-slot:actions>
        <div>
          <base-btn
            class="lg:ml-2 py-2 px-4"
            @click="$router.push({ name: 'documents' })"
          >
            <font-awesome-icon :icon="['fas', 'undo']" class="mr-1" />
            Grįžti atgal
          </base-btn>
        </div>
      </template>
    </base-page-title>

    <p v-if="errorMessage" class="text-error mb-4">
      {{ errorMessage }}
    </p>

    <form @submit.prevent="onSubmitForm">
      <div class="mb-4">
        <base-label-input
          v-model="form.title"
          type="text"
          :required="true"
          :error="failedField === 'title'"
          label="Pavadinimas"
        />
      </div>

      <div class="mb-4">
        <base-label-input
          v-model="form.description"
          type="text"
          :required="true"
          :error="failedField === 'description'"
          label="Aprašymas"
        />
      </div>

      <div class="flex flex-col lg:flex-row">
        <div class="mb-4 flex-1 lg:pr-4">
          <base-label-text label="Miestas">
            <base-select-input
              v-model="form.city"
              :blank-option="{ id: '', label: '' }"
              :options="cityOptions || []"
              required
              :error="failedField === 'city'"
            />
          </base-label-text>
        </div>
        <div class="mb-4 flex-1">
          <base-label-select
            v-model="form.role"
            :error="failedField === 'role'"
            label="Statusas"
            :options="roles"
          />
        </div>
      </div>

      <div v-if="volunteers" class="mb-4">
        <label>
          <div class="mb-2 font-medium">
            Savanoriai kurie gali redaguoti failus
          </div>

          <div>
            <base-btn-text
              v-for="editor of editors"
              :key="editor.id"
              class="inline-block px-2 py-1 mr-2"
              @click="onClickRemoveEditor(editor.id)"
            >
              {{ editor.label }}
            </base-btn-text>

            <base-btn-text
              class="px-2 py-1 inline-block lg:w-12"
              @click="onClickAddEditor"
            >
              +
            </base-btn-text>
          </div>
        </label>
      </div>

      <div class="mb-4 flex flex-col lg:flex-row">
        <div class="w-full lg:w-64 mb-4">
          <base-submit :loading="isLoading" class="btn-auth">
            {{ isEdit ? 'Atnaujinti dokumentą' : 'Sukurti dokumentą' }}
          </base-submit>
        </div>

        <div class="w-full lg:w-64 mb-4">
          <base-btn-text
            v-if="isEdit"
            class="p-4 lg:ml-4 w-full lg:w-40 text-error"
            @click="onClickDelete"
          >
            Pašalinti
          </base-btn-text>
        </div>
      </div>

      <base-dialog
        v-if="dialogs.volunteer.visible"
        title="Pridėti savanorį"
        :overlay-closes="true"
        @close="dialogs.volunteer.visible = false"
      >
        <base-label-select
          v-model="dialogs.volunteer.volunteerId"
          :options="filteredEditors"
          class="w-full mt-4"
          label="Priskirkite redaktorių dokumentui"
          required
        >
        </base-label-select>

        <template v-slot:buttons>
          <div class="w-40 relative cursor-pointer ml-4">
            <base-btn class="p-3" @click="onSubmitEditor">
              Pridėti savanorį
            </base-btn>
          </div>
        </template>
      </base-dialog>
    </form>
  </Layout>
</template>
